import {
  pollAirwallexPayment,
  toCreditCardPaymentMessage,
} from '../plugins/airwallex-payment';
import {
  AIRWALLEX_PAYMENT_METHOD_ID,
  isLockPreservingAirwallexFailure,
  resolveAirwallexPaymentIntentId,
} from './airwallex-recovery';

const INVOICE_STATUS_PAID = 1;
const INVOICE_STATUS_PENDING = 2;
const AWAITING_APPROVAL_PAYMENT_STATUS_IDS = [1, 4, 6];
const AIRWALLEX_INVOICE_RECOVERY_ACTIONS = [
  'authorize',
  'check_status',
  'finalize',
  'completed',
  'blocked',
];
const PAYNOW_PAYMENT_METHOD_ID = 6;
const PAYNOW_INVOICE_RECOVERY_ACTIONS = [
  'retry',
  'resume',
  'pending',
  'cancel',
  'blocked',
];
const PAYNOW_CANONICAL_REFRESH_POLL_INTERVAL = 30;
const PAYNOW_PENDING_CANONICAL_REFRESH_POLL_INTERVAL = 120;
const PAYNOW_MAX_CONSECUTIVE_POLL_FAILURES = 3;

function normalizedIdentifier(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();

  return normalizedValue === '' ? null : normalizedValue;
}

function normalizedSupportReference(value) {
  const reference = normalizedIdentifier(value);

  return reference
    && /^CC1-(SGD|USD|AUD)-[0-9A-Z]+-[0-9A-F]{6}$/.test(reference)
    ? reference
    : null;
}

export default {
  data() {
    return {
      airwallexInvoiceRecovery: null,
      airwallexInvoiceRecoveryLoading: false,
      airwallexInvoiceCancellationInProgress: false,
      airwallexInvoiceRecoveryUnavailable: false,
      airwallexInvoiceRecoveryRequestEpoch: 0,
      airwallexInvoiceRecoveryDestroyed: false,
      paynowPaymentRecovery: null,
      paynowInvoiceTimer: null,
      paynowInvoicePollInFlight: false,
      paynowInvoicePollErrorShown: false,
      paynowInvoicePollCount: 0,
      paynowInvoicePollFailureCount: 0,
      paynowInvoicePollingUnavailable: false,
    };
  },
  beforeDestroy() {
    this.stopPaynowInvoicePolling();
    this.airwallexInvoiceRecoveryDestroyed = true;
    this.airwallexInvoiceRecoveryRequestEpoch += 1;
    this.airwallexInvoiceRecoveryLoading = false;
    this.airwallexInvoiceCancellationInProgress = false;
  },
  computed: {
    airwallexInvoiceButtonLabel() {
      if (this.airwallexInvoiceRecoveryRequiresAuthentication) {
        return 'Sign in to continue payment';
      }

      if (this.airwallexInvoiceRecovery
        && this.airwallexInvoiceRecovery.recoverable === false
      ) {
        return 'Payment reconciliation required';
      }

      const recovery = this.airwallexInvoiceRecovery;

      if (!recovery) {
        return 'Make Payment (Visa/Master)';
      }

      return {
        authorize: recovery.reason_code === 'customer_action_required'
          ? 'Complete card authentication'
          : 'Resume credit card payment',
        check_status: 'Check credit card payment status',
        finalize: 'Complete credit card payment',
        completed: 'Completing credit card payment',
      }[recovery.action] || 'Resume credit card payment';
    },
    airwallexInvoiceRecoveryRequiresAuthentication() {
      return !!this.airwallexInvoiceRecovery
        && this.airwallexInvoiceRecovery.reason_code
          === 'authenticated_recovery_required';
    },
    airwallexInvoiceRecoveryRefreshRecommended() {
      return this.airwallexInvoiceRecoveryUnavailable
        || (
          !!this.airwallexInvoiceRecovery
          && (
            this.airwallexInvoiceRecovery.recoverable === false
            || this.airwallexInvoiceRecovery.action === 'blocked'
          )
          && !this.airwallexInvoiceRecoveryRequiresAuthentication
        );
    },
    airwallexInvoiceCanRequestPaymentMethodSwitch() {
      const recovery = this.airwallexInvoiceRecovery;

      // The gateway snapshot is advisory and can become stale while the page
      // is open. Let the guarded cancellation POST make the authoritative
      // decision for any locally authorizable, persisted intent.
      return !!recovery
        && recovery.recoverable === true
        && recovery.action === 'authorize'
        && !!recovery.payment_intent_id;
    },
    airwallexInvoiceCanSwitchPaymentMethod() {
      return this.airwallexInvoiceCanRequestPaymentMethodSwitch
        && this.airwallexInvoiceRecovery.can_switch_payment_method === true;
    },
    paynowInvoiceCanRequestPaymentMethodSwitch() {
      const recovery = this.paynowPaymentRecovery;

      return !!recovery
        && recovery.recoverable === true
        && ['retry', 'resume', 'cancel'].includes(recovery.action)
        && recovery.payment_type_id === PAYNOW_PAYMENT_METHOD_ID
        && Number.isInteger(recovery.payment_id)
        && recovery.payment_id > 0;
    },
    paynowInvoiceCanSwitchPaymentMethod() {
      return this.paynowInvoiceCanRequestPaymentMethodSwitch
        && this.paynowPaymentRecovery.can_switch_payment_method === true;
    },
    paynowInvoiceCanRetry() {
      const recovery = this.paynowPaymentRecovery;

      return !!recovery
        && recovery.recoverable === true
        && recovery.action === 'retry'
        && recovery.payment_type_id === PAYNOW_PAYMENT_METHOD_ID
        && Number.isInteger(recovery.payment_id)
        && recovery.payment_id > 0;
    },
    paynowInvoiceButtonLabel() {
      return this.paynowInvoiceCanRetry
        ? 'Retry PayNow payment'
        : 'Make Payment (Paynow)';
    },
    paynowInvoiceRecoveryRefreshRecommended() {
      return this.paynowInvoicePollingUnavailable
        || (!!this.paynowPaymentRecovery
        && !this.paynowInvoiceCanRetry
        && (
          !this.paynowInvoiceCanRequestPaymentMethodSwitch
          || !this.paynowReady
          || Number(this.paymentId) !== this.paynowPaymentRecovery.payment_id
        ));
    },
    paynowInvoiceRecoveryMessage() {
      if (!this.paynowPaymentRecovery) {
        return '';
      }

      const recovery = this.paynowPaymentRecovery;
      const localQrAvailable = this.paynowReady
        && Number(this.paymentId) === recovery.payment_id;

      if (this.paynowInvoiceCanRetry) {
        return 'The PayNow QR could not be prepared. Retry PayNow to recover '
          + 'the same payment safely, or select another payment method to '
          + 'close this reservation safely.';
      }

      if (recovery.action === 'pending') {
        return 'Your PayNow payment is processing. Do not generate another QR code. '
          + 'This page will keep checking the same payment automatically.';
      }

      if (localQrAvailable && this.paynowInvoiceCanSwitchPaymentMethod) {
        return 'A PayNow QR payment is active for this invoice. You may continue '
          + 'with PayNow, or select another payment method and the active QR will '
          + 'be cancelled safely before switching.';
      }

      if (localQrAvailable && this.paynowInvoiceCanRequestPaymentMethodSwitch) {
        return 'A PayNow QR payment is active for this invoice. You may continue '
          + 'with the displayed QR, or select another payment method; we will '
          + 'cancel the active QR only when it is safe to switch.';
      }

      if (this.paynowInvoiceCanRequestPaymentMethodSwitch) {
        return 'A PayNow payment is active for this invoice. Its existing QR code '
          + 'could not be restored, so refresh its status or select another payment '
          + 'method to cancel it safely.';
      }

      return 'A PayNow payment may already be processing for this invoice. '
        + 'Other payment methods remain locked; wait or refresh the payment status.';
    },
    airwallexInvoiceRecoveryLockRequired() {
      return this.airwallexInvoiceRecoveryLoading
        || this.airwallexInvoiceCancellationInProgress
        || this.airwallexInvoiceRecoveryUnavailable
        || (
          !!this.paynowPaymentRecovery
          && !this.paynowInvoiceCanRequestPaymentMethodSwitch
        )
        || (
          !!this.airwallexInvoiceRecovery
          && !this.airwallexInvoiceCanRequestPaymentMethodSwitch
        );
    },
    airwallexInvoiceRecoveryMessage() {
      const recovery = this.airwallexInvoiceRecovery;

      if (this.airwallexInvoiceRecoveryUnavailable) {
        return 'The invoice and payment status are temporarily unavailable. '
          + 'Refresh before continuing.';
      }

      if (this.airwallexInvoiceRecoveryRequiresAuthentication) {
        return 'Sign in to continue this saved-card payment. '
          + 'Other payment methods remain locked to prevent another charge.';
      }

      if (recovery && recovery.recoverable === false) {
        const supportReferenceSuffix = recovery.support_reference
          ? ` Reference: ${recovery.support_reference}.`
          : '';

        return [
          'This credit card payment requires reconciliation. ',
          'Other payment methods remain locked; please contact support.',
          supportReferenceSuffix,
        ].join('');
      }

      if (recovery && recovery.mode === 'status_only') {
        return 'A saved-card payment is already active for this invoice. '
          + 'You can refresh or check its status without starting another charge.';
      }

      if (!recovery) {
        return '';
      }

      let switchingMessage = ' Other payment methods remain locked until this '
        + 'payment reaches a safe state.';

      if (this.airwallexInvoiceCanSwitchPaymentMethod) {
        switchingMessage = ' You may select another payment method. The current '
          + 'card attempt will be cancelled safely before the other payment starts.';
      } else if (this.airwallexInvoiceCanRequestPaymentMethodSwitch) {
        switchingMessage = ' You may select another payment method. We will check '
          + 'the latest card status and cancel it safely before switching.';
      }

      if (recovery.action === 'authorize'
        && recovery.reason_code === 'payment_method_required'
      ) {
        const retryMessage = recovery.mode === 'new'
          ? 'The earlier card attempt did not complete. Re-enter the card details '
            + 'and select Resume credit card payment.'
          : 'The saved-card attempt did not complete. Select Resume credit card '
            + 'payment to retry it.';

        return `${retryMessage}${switchingMessage}`;
      }

      if (recovery.action === 'authorize'
        && recovery.reason_code === 'customer_action_required'
      ) {
        return 'This card payment still requires 3DS authentication. Select '
          + `Complete card authentication to finish 3DS.${switchingMessage}`;
      }

      if (recovery.action === 'finalize') {
        return 'Your card authorization is complete. Finish this credit card '
          + 'payment before selecting another payment method.';
      }

      if (recovery.action === 'check_status') {
        return 'This credit card payment is still processing. Check its status '
          + 'before selecting another payment method.';
      }

      return `A credit card payment is already active for this invoice.${
        switchingMessage
      }`;
    },
  },
  methods: {
    airwallexInvoiceDetailEndpoint() {
      throw new Error('The invoice view must provide a credit card payment detail endpoint.');
    },
    airwallexInvoiceRecoveryModes() {
      return ['new'];
    },
    airwallexInvoiceCancellationEndpoint() {
      throw new Error(
        'The invoice view must provide a credit card cancellation endpoint.',
      );
    },
    airwallexInvoiceCancellationPayload() {
      throw new Error(
        'The invoice view must provide a credit card cancellation payload.',
      );
    },
    onAirwallexInvoiceRecoveryApplied() {
    },
    onAirwallexInvoiceDetailSynced() {
    },
    paynowInvoiceStatusEndpoint() {
      throw new Error(
        'The invoice view must provide a PayNow payment status endpoint.',
      );
    },
    paynowInvoiceStatusPayload() {
      return {};
    },
    onPaynowInvoicePaymentSucceeded() {
      return this.refreshAirwallexInvoiceStatus();
    },
    stopPaynowInvoicePolling() {
      if (this.paynowInvoiceTimer === null
        || typeof this.paynowInvoiceTimer === 'undefined'
      ) {
        return;
      }

      if (typeof window !== 'undefined'
        && typeof window.clearInterval === 'function'
      ) {
        window.clearInterval(this.paynowInvoiceTimer);
      } else if (typeof clearInterval === 'function') {
        clearInterval(this.paynowInvoiceTimer);
      }

      this.paynowInvoiceTimer = null;
    },
    startPaynowInvoicePolling(paymentId) {
      const normalizedPaymentId = Number(paymentId);

      if (!Number.isInteger(normalizedPaymentId)
        || normalizedPaymentId <= 0
        || !this.isCurrentPaynowInvoicePayment(normalizedPaymentId)
        || this.paynowInvoicePollingUnavailable
        || !['resume', 'pending', 'cancel'].includes(
          this.paynowPaymentRecovery.action,
        )
      ) {
        return false;
      }

      if (this.paynowInvoiceTimer !== null
        && typeof this.paynowInvoiceTimer !== 'undefined'
      ) {
        return true;
      }

      const poll = () => {
        if (!this.canPollPaynowInvoicePayment(normalizedPaymentId)
          || this.paynowInvoicePollInFlight
        ) {
          return null;
        }

        this.paynowInvoicePollInFlight = true;
        return this.$api.post(
          this.paynowInvoiceStatusEndpoint(normalizedPaymentId),
          this.paynowInvoiceStatusPayload(normalizedPaymentId),
          { suppressErrorAlert: true },
        ).then((response) => {
          if (!this.isCurrentPaynowInvoicePayment(normalizedPaymentId)) {
            return null;
          }

          this.paynowInvoicePollErrorShown = false;
          this.paynowInvoicePollFailureCount = 0;
          if (response && response.data && response.data.success) {
            this.paynowInvoicePollCount = 0;
            this.stopPaynowInvoicePolling();
            return this.onPaynowInvoicePaymentSucceeded(
              normalizedPaymentId,
              response,
            );
          }

          this.paynowInvoicePollCount += 1;
          const pendingPayment = this.paynowPaymentRecovery.action === 'pending';
          const canonicalRefreshInterval = pendingPayment
            ? PAYNOW_PENDING_CANONICAL_REFRESH_POLL_INTERVAL
            : PAYNOW_CANONICAL_REFRESH_POLL_INTERVAL;

          if (this.paynowInvoicePollCount
            >= canonicalRefreshInterval
          ) {
            this.paynowInvoicePollCount = 0;
            return this.refreshAirwallexInvoiceRecovery(true)
              .catch((error) => {
                this.handlePaynowInvoicePollFailure(
                  normalizedPaymentId,
                  error,
                  false,
                );
                return null;
              });
          }

          return null;
        }).catch((error) => {
          this.handlePaynowInvoicePollFailure(normalizedPaymentId, error);
          return null;
        }).finally(() => {
          this.paynowInvoicePollInFlight = false;
        });
      };
      let scheduler = null;

      if (typeof window !== 'undefined'
        && typeof window.setInterval === 'function'
      ) {
        scheduler = window.setInterval.bind(window);
      } else if (typeof setInterval === 'function') {
        scheduler = setInterval;
      }

      if (!scheduler) {
        return false;
      }

      this.paynowInvoiceTimer = scheduler(poll, 1000);
      return true;
    },
    isCurrentPaynowInvoicePayment(paymentId) {
      return !this.airwallexInvoiceRecoveryDestroyed
        && !!this.paynowPaymentRecovery
        && this.paynowPaymentRecovery.payment_id === Number(paymentId);
    },
    canPollPaynowInvoicePayment(paymentId) {
      return !this.airwallexInvoiceCancellationInProgress
        && !this.paynowInvoicePollingUnavailable
        && this.isCurrentPaynowInvoicePayment(paymentId)
        && ['resume', 'pending', 'cancel'].includes(
          this.paynowPaymentRecovery.action,
        );
    },
    handlePaynowInvoicePollFailure(
      paymentId,
      error,
      refreshCanonicalStatus = true,
    ) {
      if (!this.canPollPaynowInvoicePayment(paymentId)) {
        return false;
      }

      this.paynowInvoicePollFailureCount += 1;

      if (this.paynowInvoicePollFailureCount
        < PAYNOW_MAX_CONSECUTIVE_POLL_FAILURES
      ) {
        return true;
      }

      this.stopPaynowInvoicePolling();
      this.paynowInvoicePollingUnavailable = true;

      const responseData = error && error.response
        ? error.response.data
        : null;
      const message = responseData && typeof responseData === 'object'
        ? (responseData.message || responseData)
        : (responseData || (error && error.message) || error);

      if (!this.paynowInvoicePollErrorShown) {
        this.$store.dispatch('addAlert', {
          id: Date.now(),
          type: 'error',
          message,
        });
      }
      this.paynowInvoicePollErrorShown = true;

      if (refreshCanonicalStatus) {
        this.refreshAirwallexInvoiceRecovery(true).catch(() => null);
      }

      return true;
    },
    resetPaynowInvoiceClientState() {
      this.stopPaynowInvoicePolling();
      this.paynowReady = false;
      this.paynowImage2 = '#';
      this.paymentId = null;
      this.paynowInvoicePollInFlight = false;
      this.paynowInvoicePollErrorShown = false;
      this.paynowInvoicePollCount = 0;
      this.paynowInvoicePollFailureCount = 0;
      this.paynowInvoicePollingUnavailable = false;
    },
    clearPaynowInvoiceClientState(clearRecovery = true) {
      this.resetPaynowInvoiceClientState();

      if (clearRecovery) {
        this.paynowPaymentRecovery = null;
      }

      this.loading = false;
    },
    syncAirwallexInvoicePresentation(detail) {
      const currency = typeof window.systemCurrency === 'string'
        ? window.systemCurrency.toUpperCase()
        : '';
      const items = detail.invoiceItems.map((invoiceItem) => ({
        item_id: invoiceItem.id,
        item_name: `${detail.invoiceDatetime} ${invoiceItem.invoiceLineDescription}`,
        price: invoiceItem.subtotal,
        discount: invoiceItem.discount,
        tax: invoiceItem.tax,
        quantity: 1,
      }));

      this.$set(this, 'gtagObj', {
        currency,
        value: detail.total,
        discount: detail.discount,
        tax: detail.tax,
        items,
      });

      const invoiceNumber = encodeURIComponent(detail.invoiceNo);
      const amount = encodeURIComponent(detail.total);

      this.$api.get(
        `paynow-image?invoice=${invoiceNumber}&amount=${amount}`,
        { suppressErrorAlert: true },
      ).then((response) => {
        if (!this.airwallexInvoiceRecoveryDestroyed
          && this.invoice === detail
        ) {
          this.paynowImage = response.data;
        }
      }).catch(() => {
        // QR rendering is ancillary; the next detail refresh retries it.
      });
    },
    pollAirwallexInvoicePayment(sendRequest, initialResponse = null) {
      return pollAirwallexPayment(
        sendRequest,
        initialResponse,
        undefined,
        () => !this.airwallexInvoiceRecoveryDestroyed,
      );
    },
    fetchAirwallexInvoiceDetail() {
      return this.$api.get(this.airwallexInvoiceDetailEndpoint(), {
        suppressErrorAlert: true,
      });
    },
    isCurrentAirwallexInvoiceRecoveryRequest(requestEpoch) {
      return !this.airwallexInvoiceRecoveryDestroyed
        && requestEpoch === this.airwallexInvoiceRecoveryRequestEpoch;
    },
    applyAirwallexInvoiceRecovery(recovery) {
      if (this.airwallexInvoiceRecoveryDestroyed) {
        return this.airwallexInvoiceRecovery;
      }

      if (recovery === null) {
        this.airwallexInvoiceRecovery = null;
        return null;
      }

      if (!recovery
        || typeof recovery !== 'object'
        || Array.isArray(recovery)
      ) {
        throw new Error(
          'The credit card payment recovery status is invalid.',
        );
      }

      const declaredRecoverable = recovery.recoverable !== false;
      const supportedModes = this.airwallexInvoiceRecoveryModes();
      const candidateMode = declaredRecoverable
        && supportedModes.includes(recovery.mode)
        ? recovery.mode
        : null;
      let fallbackAction = 'blocked';

      if (declaredRecoverable && candidateMode !== null) {
        fallbackAction = candidateMode === 'status_only'
          ? 'check_status'
          : 'authorize';
      }
      const suppliedAction = normalizedIdentifier(recovery.action);
      let action = fallbackAction;

      if (suppliedAction !== null) {
        action = AIRWALLEX_INVOICE_RECOVERY_ACTIONS.includes(suppliedAction)
          ? suppliedAction
          : 'blocked';
      }
      const recoverable = declaredRecoverable && action !== 'blocked';
      const mode = recoverable ? candidateMode : null;
      const hasPaymentStatus = recovery.payment_status_id !== null
        && typeof recovery.payment_status_id !== 'undefined'
        && String(recovery.payment_status_id).trim() !== '';
      const normalizedRecovery = {
        payment_type_id: Number(recovery.payment_type_id) === 8 ? 8 : AIRWALLEX_PAYMENT_METHOD_ID,
        action,
        payment_intent_id: normalizedIdentifier(
          recovery.payment_intent_id,
        ),
        mode,
        recoverable,
        payment_status_id: hasPaymentStatus
          && Number.isFinite(Number(recovery.payment_status_id))
          ? Number(recovery.payment_status_id)
          : null,
        reason_code: normalizedIdentifier(recovery.reason_code),
        can_switch_payment_method: recoverable
          && action === 'authorize'
          && !!normalizedIdentifier(recovery.payment_intent_id)
          && recovery.can_switch_payment_method === true,
        support_reference: normalizedSupportReference(
          recovery.support_reference,
        ),
      };

      if (supportedModes.includes('saved')) {
        normalizedRecovery.payment_card_id = mode === 'saved'
          && Number.isFinite(Number(recovery.payment_card_id))
          && Number(recovery.payment_card_id) > 0
          ? Number(recovery.payment_card_id)
          : null;
      }

      this.airwallexInvoiceRecovery = normalizedRecovery;
      this.paymentMethod = normalizedRecovery.payment_type_id;
      this.onAirwallexInvoiceRecoveryApplied(normalizedRecovery);
      return normalizedRecovery;
    },
    applyPaynowInvoiceRecovery(recovery) {
      if (this.airwallexInvoiceRecoveryDestroyed) {
        return this.paynowPaymentRecovery;
      }

      if (recovery === null) {
        this.resetPaynowInvoiceClientState();
        this.paynowPaymentRecovery = null;
        return null;
      }

      if (!recovery
        || typeof recovery !== 'object'
        || Array.isArray(recovery)
      ) {
        throw new Error('The PayNow payment recovery status is invalid.');
      }

      const paymentId = Number(recovery.payment_id);
      const paymentTypeId = Number(recovery.payment_type_id);
      const suppliedAction = normalizedIdentifier(recovery.action);
      const action = suppliedAction !== null
        && PAYNOW_INVOICE_RECOVERY_ACTIONS.includes(suppliedAction)
        ? suppliedAction
        : 'blocked';
      const validPayment = Number.isInteger(paymentId)
        && paymentId > 0
        && paymentTypeId === PAYNOW_PAYMENT_METHOD_ID;
      const switchableAction = ['retry', 'resume', 'cancel'].includes(action);
      const recoverable = recovery.recoverable === true
        && switchableAction
        && validPayment;
      const pending = action === 'pending' && validPayment;
      const hasPaymentStatus = recovery.payment_status_id !== null
        && typeof recovery.payment_status_id !== 'undefined'
        && String(recovery.payment_status_id).trim() !== '';
      let normalizedAction = 'blocked';

      if (pending) {
        normalizedAction = 'pending';
      } else if (recoverable) {
        normalizedAction = action;
      }

      const normalizedRecovery = {
        action: normalizedAction,
        payment_id: validPayment ? paymentId : null,
        payment_type_id: validPayment ? paymentTypeId : PAYNOW_PAYMENT_METHOD_ID,
        payment_status_id: hasPaymentStatus
          && Number.isFinite(Number(recovery.payment_status_id))
          ? Number(recovery.payment_status_id)
          : null,
        recoverable,
        can_switch_payment_method: recoverable
          && switchableAction
          && recovery.can_switch_payment_method === true,
        reason_code: normalizedIdentifier(recovery.reason_code),
        gateway_status: normalizedIdentifier(recovery.gateway_status),
        paynow_image: normalizedIdentifier(recovery.paynow_image),
        payment_pending: recovery.payment_pending === true
          || action === 'pending',
      };

      const currentPaymentId = this.paynowPaymentRecovery
        ? this.paynowPaymentRecovery.payment_id
        : Number(this.paymentId);

      if (Number.isInteger(currentPaymentId)
        && currentPaymentId > 0
        && currentPaymentId !== normalizedRecovery.payment_id
      ) {
        this.resetPaynowInvoiceClientState();
      }

      this.paynowPaymentRecovery = normalizedRecovery;
      this.paymentId = normalizedRecovery.payment_id;
      this.paymentMethod = PAYNOW_PAYMENT_METHOD_ID;

      if (!['resume', 'pending', 'cancel'].includes(normalizedRecovery.action)) {
        this.stopPaynowInvoicePolling();
      }

      if (normalizedRecovery.paynow_image) {
        this.paynowImage2 = normalizedRecovery.paynow_image;
        this.paynowReady = true;
      } else if (currentPaymentId !== normalizedRecovery.payment_id
        || ['pending', 'blocked', 'retry'].includes(normalizedRecovery.action)
      ) {
        this.paynowImage2 = '#';
        this.paynowReady = false;
      }

      return normalizedRecovery;
    },
    rememberPaynowInvoiceRecovery(
      paymentId,
      recovery = null,
      paymentResponse = null,
    ) {
      const suppliedRecovery = recovery
        && typeof recovery === 'object'
        && !Array.isArray(recovery)
        ? recovery
        : {};
      const suppliedResponse = paymentResponse
        && typeof paymentResponse === 'object'
        && !Array.isArray(paymentResponse)
        ? paymentResponse
        : {};
      const paynowImage = normalizedIdentifier(
        suppliedRecovery.paynow_image || suppliedResponse.paynow_image,
      );
      const paymentPending = suppliedRecovery.payment_pending === true
        || suppliedResponse.payment_pending === true;
      let fallbackAction = 'cancel';

      if (paymentPending) {
        fallbackAction = 'pending';
      } else if (paynowImage) {
        fallbackAction = 'resume';
      }

      return this.applyPaynowInvoiceRecovery({
        ...suppliedRecovery,
        action: suppliedRecovery.action || fallbackAction,
        payment_id: suppliedRecovery.payment_id || paymentId,
        payment_type_id: PAYNOW_PAYMENT_METHOD_ID,
        payment_status_id: typeof suppliedRecovery.payment_status_id === 'undefined'
          ? 4
          : suppliedRecovery.payment_status_id,
        recoverable: typeof suppliedRecovery.recoverable === 'undefined'
          ? !paymentPending
          : suppliedRecovery.recoverable === true,
        can_switch_payment_method:
          typeof suppliedRecovery.can_switch_payment_method === 'undefined'
            ? !paymentPending
            : suppliedRecovery.can_switch_payment_method === true,
        gateway_status: suppliedRecovery.gateway_status
          || suppliedResponse.paynow_status,
        paynow_image: paynowImage,
        payment_pending: paymentPending,
      });
    },
    rememberPaynowInvoicePaymentResponse(paymentResponse) {
      if (!paymentResponse
        || typeof paymentResponse !== 'object'
        || Array.isArray(paymentResponse)
      ) {
        throw new Error('The PayNow payment response is invalid.');
      }

      const paymentId = Number(paymentResponse.payment_id);

      if (!Number.isInteger(paymentId) || paymentId <= 0) {
        throw new Error('The PayNow payment response is incomplete.');
      }

      const recovery = this.rememberPaynowInvoiceRecovery(
        paymentId,
        paymentResponse.paynowPaymentRecovery,
        paymentResponse,
      );

      if (['resume', 'pending', 'cancel'].includes(recovery.action)) {
        this.startPaynowInvoicePolling(paymentId);
      }
      return recovery;
    },
    legacyPaynowInvoiceRecovery(detail, airwallexRecovery) {
      const hasPaymentId = Object.prototype.hasOwnProperty.call(
        detail,
        'paymentId',
      );
      const paymentId = Number(detail.paymentId);
      const validPaymentId = Number.isInteger(paymentId) && paymentId > 0;
      const { payment } = detail;
      const validPaymentObject = !!payment
        && typeof payment === 'object'
        && !Array.isArray(payment);
      const paymentTypeId = validPaymentObject
        ? Number(payment.paymentTypeId)
        : null;
      const knownRecovery = this.paynowPaymentRecovery;

      if (validPaymentId
        && knownRecovery
        && knownRecovery.payment_id === paymentId
      ) {
        return knownRecovery;
      }

      if (paymentTypeId === PAYNOW_PAYMENT_METHOD_ID) {
        return {
          payment_id: validPaymentId ? paymentId : null,
          payment_type_id: PAYNOW_PAYMENT_METHOD_ID,
          payment_status_id: payment.paymentStatusId,
          recoverable: false,
          action: 'blocked',
          can_switch_payment_method: false,
        };
      }

      const noAttachedPayment = hasPaymentId && !validPaymentId;
      const attachedDifferentMethod = validPaymentObject
        && Number.isInteger(paymentTypeId)
        && paymentTypeId !== PAYNOW_PAYMENT_METHOD_ID;

      if (airwallexRecovery !== null
        || noAttachedPayment
        || attachedDifferentMethod
      ) {
        return null;
      }

      if (knownRecovery) {
        return knownRecovery;
      }

      throw new Error('The PayNow payment recovery status is unavailable.');
    },
    markAirwallexInvoiceRecoveryUnavailable() {
      this.airwallexInvoiceRecoveryUnavailable = true;
      return this.airwallexInvoiceRecovery;
    },
    rememberAirwallexInvoiceRecovery({
      action = 'authorize',
      mode = 'new',
      paymentCardId = null,
      paymentIntentId = null,
    } = {}) {
      const currentRecovery = this.airwallexInvoiceRecovery || {};

      this.applyAirwallexInvoiceRecovery({
        action,
        mode,
        payment_card_id: paymentCardId,
        payment_intent_id: paymentIntentId || currentRecovery.payment_intent_id,
        recoverable: currentRecovery.recoverable !== false,
        payment_status_id: currentRecovery.payment_status_id || null,
        reason_code: currentRecovery.reason_code || null,
        can_switch_payment_method:
          currentRecovery.can_switch_payment_method === true,
        support_reference: currentRecovery.support_reference || null,
      });
    },
    airwallexInvoiceCancellationErrorMessage(error) {
      const responseData = error && error.response
        ? error.response.data
        : null;
      const message = responseData && typeof responseData === 'object'
        ? responseData.message
        : responseData;
      const customerMessage = toCreditCardPaymentMessage(
        message || (error && error.message) || error,
      );

      const messageSuffix = customerMessage
        ? ` ${customerMessage}`
        : ' Please retry.';

      return `The payment method could not be changed safely.${messageSuffix}`;
    },
    async cancelAirwallexInvoiceRecoveryForPaymentSwitch(
      recovery = this.airwallexInvoiceRecovery,
    ) {
      if (!recovery
        || recovery.recoverable !== true
        || recovery.action !== 'authorize'
        || !recovery.payment_intent_id
        || this.airwallexInvoiceCancellationInProgress
      ) {
        return false;
      }

      this.airwallexInvoiceCancellationInProgress = true;
      // An older detail request must not restore the attempt after the server
      // has confirmed its cancellation.
      this.airwallexInvoiceRecoveryRequestEpoch += 1;

      try {
        const response = await this.$api.post(
          this.airwallexInvoiceCancellationEndpoint(),
          this.airwallexInvoiceCancellationPayload(recovery),
          { suppressErrorAlert: true },
        );

        if (!response
          || !response.data
          || response.data.success !== true
        ) {
          throw new Error(
            'The credit card payment cancellation response is incomplete.',
          );
        }

        this.airwallexInvoiceRecoveryUnavailable = false;
        this.applyAirwallexInvoiceRecovery(null);
        return true;
      } catch (error) {
        this.paymentMethod = Number((this.airwallexInvoiceRecovery || recovery).payment_type_id)
          || AIRWALLEX_PAYMENT_METHOD_ID;

        try {
          await this.refreshAirwallexInvoiceRecovery(true);
        } catch (refreshError) {
          // A failed canonical refresh must retain the recovery lock.
        }

        if (!this.airwallexInvoiceRecoveryDestroyed) {
          this.paymentMethod = Number((this.airwallexInvoiceRecovery || recovery).payment_type_id)
          || AIRWALLEX_PAYMENT_METHOD_ID;
          this.$store.dispatch('addAlert', {
            id: Date.now(),
            type: 'error',
            message: this.airwallexInvoiceCancellationErrorMessage(error),
          });
        }

        return false;
      } finally {
        this.airwallexInvoiceCancellationInProgress = false;
      }
    },
    async cancelPaynowInvoiceRecoveryForPaymentSwitch(
      recovery = this.paynowPaymentRecovery,
    ) {
      if (!recovery
        || recovery.recoverable !== true
        || !['retry', 'resume', 'cancel'].includes(recovery.action)
        || recovery.payment_type_id !== PAYNOW_PAYMENT_METHOD_ID
        || !Number.isInteger(recovery.payment_id)
        || recovery.payment_id <= 0
        || this.airwallexInvoiceCancellationInProgress
      ) {
        return false;
      }

      this.airwallexInvoiceCancellationInProgress = true;
      this.airwallexInvoiceRecoveryRequestEpoch += 1;

      try {
        const response = await this.$api.post(
          this.airwallexInvoiceCancellationEndpoint(),
          this.airwallexInvoiceCancellationPayload(recovery),
          { suppressErrorAlert: true },
        );

        if (!response
          || !response.data
          || response.data.success !== true
        ) {
          throw new Error(
            'The PayNow payment cancellation response is incomplete.',
          );
        }

        this.airwallexInvoiceRecoveryUnavailable = false;
        this.clearPaynowInvoiceClientState();
        return true;
      } catch (error) {
        this.paymentMethod = PAYNOW_PAYMENT_METHOD_ID;
        let refreshResponse = null;

        try {
          refreshResponse = await this.refreshAirwallexInvoiceRecovery(true);
        } catch (refreshError) {
          // A failed canonical refresh must retain the PayNow lock below.
        }

        const refreshHasPaynowRecovery = !!refreshResponse
          && !!refreshResponse.data
          && Object.prototype.hasOwnProperty.call(
            refreshResponse.data,
            'paynowPaymentRecovery',
          );
        const paynowCanonicallyReleased = refreshHasPaynowRecovery
          && !this.paynowPaymentRecovery;

        if (paynowCanonicallyReleased) {
          this.clearPaynowInvoiceClientState();

          if (!this.airwallexInvoiceRecovery) {
            return true;
          }
        }

        if (!this.paynowPaymentRecovery
          && !this.airwallexInvoiceRecovery
          && !refreshHasPaynowRecovery
        ) {
          this.applyPaynowInvoiceRecovery(recovery);
        }

        if (!this.airwallexInvoiceRecoveryDestroyed) {
          this.paymentMethod = this.airwallexInvoiceRecovery
            ? (Number(this.airwallexInvoiceRecovery.payment_type_id) || AIRWALLEX_PAYMENT_METHOD_ID)
            : PAYNOW_PAYMENT_METHOD_ID;
          this.$store.dispatch('addAlert', {
            id: Date.now(),
            type: 'error',
            message: this.airwallexInvoiceCancellationErrorMessage(error),
          });
        }

        return false;
      } finally {
        this.airwallexInvoiceCancellationInProgress = false;
      }
    },
    async selectAirwallexInvoicePaymentMethod(paymentMethod) {
      if (Number(paymentMethod) === Number(this.paymentMethod)) {
        return true;
      }

      const paynowRecovery = this.paynowPaymentRecovery;

      if (paynowRecovery) {
        this.paymentMethod = PAYNOW_PAYMENT_METHOD_ID;

        if (Number(paymentMethod) === PAYNOW_PAYMENT_METHOD_ID
          || !this.paynowInvoiceCanRequestPaymentMethodSwitch
        ) {
          return false;
        }

        const paynowReleased = await this
          .cancelPaynowInvoiceRecoveryForPaymentSwitch(paynowRecovery);

        if (!paynowReleased || this.airwallexInvoiceRecoveryDestroyed) {
          this.paymentMethod = this.airwallexInvoiceRecovery
            ? (Number(this.airwallexInvoiceRecovery.payment_type_id) || AIRWALLEX_PAYMENT_METHOD_ID)
            : PAYNOW_PAYMENT_METHOD_ID;
          return false;
        }

        this.paymentMethod = paymentMethod;
        return true;
      }

      const recovery = this.airwallexInvoiceRecovery;

      if (!recovery) {
        this.paymentMethod = paymentMethod;
        return true;
      }

      this.paymentMethod = Number((this.airwallexInvoiceRecovery || recovery).payment_type_id)
          || AIRWALLEX_PAYMENT_METHOD_ID;

      if (Number(paymentMethod)
          === (Number(recovery.payment_type_id) || AIRWALLEX_PAYMENT_METHOD_ID)
        || !this.airwallexInvoiceCanRequestPaymentMethodSwitch
      ) {
        return false;
      }

      const released = await this
        .cancelAirwallexInvoiceRecoveryForPaymentSwitch(recovery);

      if (!released || this.airwallexInvoiceRecoveryDestroyed) {
        this.paymentMethod = Number((this.airwallexInvoiceRecovery || recovery).payment_type_id)
          || AIRWALLEX_PAYMENT_METHOD_ID;
        return false;
      }

      this.paymentMethod = paymentMethod;
      return true;
    },
    syncAirwallexInvoiceDetail(detail, preserveRecoveryWhenMissing = false) {
      if (this.airwallexInvoiceRecoveryDestroyed) {
        return;
      }

      if (!detail
        || typeof detail !== 'object'
        || !Object.prototype.hasOwnProperty.call(detail, 'total')
        || !Array.isArray(detail.invoiceItems)
      ) {
        throw new Error(
          'The invoice and credit card payment status response is incomplete.',
        );
      }

      const hasRecoveryProperty = Object.prototype.hasOwnProperty.call(
        detail,
        'airwallexPaymentRecovery',
      );

      if (!hasRecoveryProperty) {
        throw new Error(
          preserveRecoveryWhenMissing
            ? 'The credit card payment recovery status is unavailable.'
            : 'The credit card payment recovery status is missing.',
        );
      }

      const recovery = detail.airwallexPaymentRecovery;
      const hasPaynowRecoveryProperty = Object.prototype.hasOwnProperty.call(
        detail,
        'paynowPaymentRecovery',
      );
      const paynowRecovery = hasPaynowRecoveryProperty
        ? detail.paynowPaymentRecovery
        : this.legacyPaynowInvoiceRecovery(detail, recovery);

      if (recovery !== null
        && (
          !recovery
          || typeof recovery !== 'object'
          || Array.isArray(recovery)
        )
      ) {
        throw new Error(
          'The credit card payment recovery status is invalid.',
        );
      }

      if (paynowRecovery !== null
        && (
          !paynowRecovery
          || typeof paynowRecovery !== 'object'
          || Array.isArray(paynowRecovery)
        )
      ) {
        throw new Error('The PayNow payment recovery status is invalid.');
      }

      if (recovery !== null && paynowRecovery !== null) {
        throw new Error('The invoice payment recovery status is inconsistent.');
      }

      this.amount = detail.total;
      this.$set(this, 'invoice', detail);
      this.$set(this, 'items', detail.invoiceItems);
      this.applyAirwallexInvoiceRecovery(recovery);
      const normalizedPaynowRecovery = this.applyPaynowInvoiceRecovery(
        paynowRecovery,
      );

      if (normalizedPaynowRecovery
        && normalizedPaynowRecovery.payment_id
        && ['resume', 'pending', 'cancel'].includes(
          normalizedPaynowRecovery.action,
        )
      ) {
        this.startPaynowInvoicePolling(
          normalizedPaynowRecovery.payment_id,
        );
      }

      this.onAirwallexInvoiceDetailSynced(detail);
    },
    resolveAirwallexInvoicePaymentIntentId(response) {
      const responseData = response && response.data
        && typeof response.data === 'object'
        ? response.data
        : {};
      const hasRecovery = Object.prototype.hasOwnProperty.call(
        responseData,
        'airwallexPaymentRecovery',
      );
      const recovery = hasRecovery
        && responseData.airwallexPaymentRecovery
        && typeof responseData.airwallexPaymentRecovery === 'object'
        ? responseData.airwallexPaymentRecovery
        : null;
      const fallbackRecovery = this.airwallexInvoiceRecovery;

      return resolveAirwallexPaymentIntentId(
        responseData.payment_intent_id,
        recovery && recovery.payment_intent_id,
        fallbackRecovery && fallbackRecovery.payment_intent_id,
      );
    },
    refreshAirwallexInvoiceRecovery(preserveWhenMissing = false) {
      const requestEpoch = this.airwallexInvoiceRecoveryRequestEpoch + 1;

      this.airwallexInvoiceRecoveryRequestEpoch = requestEpoch;
      this.airwallexInvoiceRecoveryLoading = true;

      return Promise.resolve()
        .then(() => this.fetchAirwallexInvoiceDetail())
        .then((response) => {
          if (this.isCurrentAirwallexInvoiceRecoveryRequest(requestEpoch)) {
            this.syncAirwallexInvoiceDetail(
              response.data,
              preserveWhenMissing,
            );
            this.airwallexInvoiceRecoveryUnavailable = false;
          }

          return response;
        })
        .catch((error) => {
          if (this.isCurrentAirwallexInvoiceRecoveryRequest(requestEpoch)) {
            this.markAirwallexInvoiceRecoveryUnavailable();
          }

          throw error;
        })
        .finally(() => {
          if (this.isCurrentAirwallexInvoiceRecoveryRequest(requestEpoch)) {
            this.airwallexInvoiceRecoveryLoading = false;
          }
        });
    },
    airwallexInvoiceRefreshErrorMessage(error) {
      const responseData = error && error.response
        ? error.response.data
        : null;
      const message = responseData && typeof responseData === 'object'
        ? responseData.message
        : responseData;
      const customerMessage = toCreditCardPaymentMessage(
        message || (error && error.message) || error,
      );

      return `The invoice and payment status could not be refreshed.${
        customerMessage ? ` ${customerMessage}` : ''
      }`;
    },
    refreshAirwallexInvoiceStatus() {
      if (this.airwallexInvoiceRecoveryLoading
        || this.airwallexInvoiceRecoveryDestroyed
      ) {
        return Promise.resolve(null);
      }

      this.paynowInvoicePollingUnavailable = false;
      this.paynowInvoicePollErrorShown = false;
      this.paynowInvoicePollCount = 0;
      this.paynowInvoicePollFailureCount = 0;

      return this.refreshAirwallexInvoiceRecovery()
        .catch((error) => {
          if (!this.airwallexInvoiceRecoveryDestroyed) {
            this.$store.dispatch('addAlert', {
              id: Date.now(),
              type: 'error',
              message: this.airwallexInvoiceRefreshErrorMessage(error),
            });
          }

          return null;
        });
    },
    async handleAirwallexInvoiceError(error) {
      const preserveRecoveryWhenMissing = isLockPreservingAirwallexFailure(
        error,
      );
      let refreshResponse = null;

      try {
        refreshResponse = await this.refreshAirwallexInvoiceRecovery(
          preserveRecoveryWhenMissing,
        );
      } catch (refreshError) {
        // Keep the provisional lock until the server can prove that the
        // invoice no longer has an active Airwallex attempt.
      }

      if (refreshResponse
        && Number(refreshResponse.data.invoiceStatusId) === INVOICE_STATUS_PAID
      ) {
        this.airwallexInvoiceRecovery = null;
        this.$store.dispatch('addAlert', {
          id: Date.now(),
          type: 'success',
          message: 'Your payment is submitted successfully.',
        });
        this.reset();
        return;
      }

      const refreshedRecovery = refreshResponse
        ? refreshResponse.data.airwallexPaymentRecovery
        : null;
      const awaitingApproval = refreshResponse
        && Number(refreshResponse.data.invoiceStatusId) === INVOICE_STATUS_PENDING
        && refreshedRecovery
        && refreshedRecovery.recoverable === true
        && AWAITING_APPROVAL_PAYMENT_STATUS_IDS.includes(
          Number(refreshedRecovery.payment_status_id),
        );

      if (awaitingApproval) {
        this.$store.dispatch('addAlert', {
          id: Date.now(),
          type: 'success',
          message: 'Your payment is submitted successfully and is awaiting approval.',
        });
        this.reset();
        return;
      }

      this.resetAirwallexRetryConsent();
      const message = error && error.response
        ? (error.response.data.message || error.response.data)
        : ((error && error.message) || error);
      const customerMessage = toCreditCardPaymentMessage(message);

      this.$store.dispatch('addAlert', {
        id: Date.now(),
        type: 'error',
        message: `An error occurred ${customerMessage || ''}. Please retry again.`,
      });
    },
    resetAirwallexRetryConsent() {
      if (this.$store.state.brand === 'cldy') {
        if (typeof this.tosAccepted !== 'undefined') {
          this.tosAccepted = false;
        }

        if (typeof this.tosAuditSucceeded !== 'undefined') {
          this.tosAuditSucceeded = false;
        }
      }
    },
  },
};
